/**
 * ČESKÁ ERROR BOUNDARY ADMINISTRACE (krok UXP1, nalez P1-3).
 *
 * Kdyz komponenta vyhodila vyjimku, router kreslil vlastni anglicke
 * „Something went wrong!" s tlacitkem „Show Error" — makler nevedel, co se
 * stalo, ani jak pokracovat (rucni pruchod 10. 10., inzerat pozemku). Tohle je
 * tatáž obrazovka cesky a s akci, ktera stav skutecne obnovi.
 *
 * Technicky text chyby ZUSTAVA, ale sbaleny v `<details>` — patri do hlaseni
 * pro podporu, ne na prvni pohled.
 *
 * KROK CER1 bod 1 — tahle obrazovka je ERROR BOUNDARY administrace (router ji
 * kresli jako `defaultErrorComponent`, `router.tsx`). Od CER1 chybu zaroven
 * ZAPISE k nam (`POST /v1/client-errors`, kind `boundary`) a makleři to rekne
 * („Chyba je zapsaná"), aby nemusel nic opisovat. Zapis je jednorazovy pro
 * jedno vykresleni — `useEffect` se zavislosti na chybe.
 */
import * as React from 'react';
import { t } from '@reos/i18n';
import { reportClientError } from '../lib/client-error-reporter.js';

const cs = { locale: 'cs' } as const;

export function AppErrorScreen({
  error,
  reset,
}: {
  readonly error: unknown;
  readonly reset?: (() => void) | undefined;
}): React.JSX.Element {
  const detail = error instanceof Error ? error.message : String(error);
  React.useEffect(() => {
    reportClientError({
      kind: 'boundary',
      message: detail,
      ...(error instanceof Error ? { stack: error.stack } : {}),
    });
  }, [detail, error]);
  return (
    <section className="rh-card" role="alert" data-testid="app-error">
      <h1 className="rh-overview__title" style={{ marginTop: 0 }}>
        {t('webAdmin.appError.title', cs)}
      </h1>
      <p className="text-sm" style={{ color: 'var(--rh-text-muted)' }}>
        {t('webAdmin.appError.lede', cs)}
      </p>
      <p className="text-sm" style={{ color: 'var(--rh-text-muted)' }} data-testid="app-error-logged">
        {t('webAdmin.appError.logged', cs)}
      </p>
      <div className="flex gap-2">
        {reset === undefined ? null : (
          <button type="button" className="rh-btn" onClick={reset} data-testid="app-error-retry">
            {t('webAdmin.appError.retry', cs)}
          </button>
        )}
        <button
          type="button"
          className="rh-btn rh-btn--ghost"
          onClick={() => { window.location.reload(); }}
          data-testid="app-error-reload"
        >
          {t('webAdmin.appError.reload', cs)}
        </button>
      </div>
      <details className="mt-3">
        <summary className="text-sm" style={{ cursor: 'pointer' }}>
          {t('webAdmin.appError.detailSummary', cs)}
        </summary>
        <p className="text-sm m-0 mt-2" data-testid="app-error-detail" style={{ wordBreak: 'break-word' }}>
          {detail}
        </p>
      </details>
    </section>
  );
}
